import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  Select,
  Toast,
  ToastTitle,
  Toaster,
  useId,
  useToastController,
} from "@fluentui/react-components";
import {
  Add20Regular,
  ArrowSync20Regular,
  Checkmark20Regular,
  ChevronLeft20Regular,
  ChevronRight20Regular,
  Delete20Regular,
  Dismiss20Regular,
  Dismiss24Regular,
  Edit20Regular,
  Search20Regular,
} from "@fluentui/react-icons";
// Same client the Retail & POS screen uses, so routes, sign-in and row versions behave exactly the same.
// If your Retail folder has a different name, change only this line.
import { crud, errorMessage } from "../Retails/api/retailApi";
import styles from "./Settup.module.css";

/* ------------------------------------------------------------------ */
/* Retail & POS setup screens.                                         */
/* Everything the point of sale needs before the first sale: stores,   */
/* registers (the "Which till is this?" list), staff, payment methods, */
/* tax, currency, categories and suppliers.                            */
/* ------------------------------------------------------------------ */

type Row = { id: string; rowVersion?: string; [k: string]: any };
type Intent = "success" | "error" | "warning";
type Opt = { v: string; l: string };
type Lk = "stores" | "jurisdictions" | "taxCategories";

interface Fld {
  key: string;
  label: string;
  kind: "text" | "number" | "select" | "datetime";
  options?: Opt[];
  lookup?: Lk;
  required?: boolean;
  full?: boolean;
  /** stored as a fraction (0.075), edited as a percentage (7.5) */
  pct?: boolean;
  hint?: string;
}
interface Col {
  key: string;
  label: string;
  kind?: "mono" | "status" | "lookup" | "pct" | "date" | "num";
  lookup?: Lk;
  right?: boolean;
  get?: (r: Row) => any;
}
interface Entity {
  title: string;
  subtitle: string;
  noun: string;
  route: string;
  lookups?: Lk[];
  /** server-side filter on this field */
  status?: { key: string; options: Opt[] };
  /** how the row is switched on and off */
  toggle?: "active" | "register";
  defaults?: (currency: string) => Record<string, any>;
  cols: Col[];
  fields: Fld[];
  validate?: (v: Record<string, any>) => string | null;
}

const LOOKUPS: Record<Lk, { route: string; label: (r: Row) => string }> = {
  stores: { route: "organization/stores", label: (r) => String(r.name) },
  jurisdictions: { route: "tax/jurisdictions", label: (r) => String(r.name) },
  taxCategories: { route: "tax/categories", label: (r) => String(r.name) },
};

const spaced = (s: string) => s.replace(/([a-z])([A-Z])/g, "$1 $2");
const st = (...a: string[]): Opt[] => a.map((v) => ({ v, l: spaced(v) }));
const ACTIVE_OPTS: Opt[] = [
  { v: "true", l: "Active" },
  { v: "false", l: "Inactive" },
];
const num = (v: any): number => (typeof v === "number" ? v : Number(v) || 0);
const activeCol: Col = { key: "isActive", label: "Status", kind: "status", get: (r) => (r.isActive === false ? "Inactive" : "Active") };

const ENTITIES: Record<string, Entity> = {
  stores: {
    title: "Stores",
    subtitle: "Stores, warehouses and distribution centres. Add a store first, then its registers.",
    noun: "store",
    route: "organization/stores",
    status: { key: "isActive", options: ACTIVE_OPTS },
    toggle: "active",
    defaults: (cur) => ({ type: "Store", countryCode: "NG", currencyCode: cur, timeZoneId: "Africa/Lagos", cultureCode: "en", isActive: true }),
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "type", label: "Type" },
      { key: "city", label: "City" },
      { key: "currencyCode", label: "Currency" },
      activeCol,
    ],
    fields: [
      { key: "code", label: "Code", kind: "text", required: true },
      { key: "name", label: "Name", kind: "text", required: true },
      { key: "type", label: "Type", kind: "select", options: st("Store", "DistributionCenter", "Warehouse", "DarkStore"), required: true },
      { key: "city", label: "City", kind: "text" },
      { key: "countryCode", label: "Country code", kind: "text", required: true, hint: "Two letters, for example NG" },
      { key: "currencyCode", label: "Currency", kind: "text", required: true, hint: "Three letters, for example NGN" },
      { key: "timeZoneId", label: "Time zone", kind: "text", required: true, full: true },
    ],
  },
  registers: {
    title: "Registers",
    subtitle: "The tills, mobile devices, self-checkouts and kiosks that take payments. Each one appears in the \"Which till is this?\" list on the point of sale.",
    noun: "register",
    route: "organization/registers",
    lookups: ["stores"],
    status: { key: "status", options: st("Active", "Offline", "Maintenance", "Retired") },
    toggle: "register",
    defaults: () => ({ status: "Active" }),
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "store", label: "Store", kind: "lookup", lookup: "stores", get: (r) => r.storeId },
      { key: "type", label: "Type" },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "storeId", label: "Store", kind: "select", lookup: "stores", required: true, full: true },
      { key: "code", label: "Code", kind: "text", required: true },
      { key: "name", label: "Name", kind: "text", required: true },
      { key: "type", label: "Type", kind: "select", options: st("Pos", "MobilePos", "SelfCheckout", "Kiosk"), required: true },
    ],
  },
  associates: {
    title: "Staff",
    subtitle: "Cashiers and store associates. Their logins live in the central auth service.",
    noun: "staff member",
    route: "organization/associates",
    lookups: ["stores"],
    status: { key: "isActive", options: ACTIVE_OPTS },
    toggle: "active",
    defaults: (cur) => ({ currencyCode: cur, isActive: true }),
    cols: [
      { key: "employeeNo", label: "Employee", kind: "mono" },
      { key: "displayName", label: "Name" },
      { key: "title", label: "Title" },
      { key: "store", label: "Home store", kind: "lookup", lookup: "stores", get: (r) => r.homeStoreId },
      { key: "phone", label: "Phone" },
      activeCol,
    ],
    fields: [
      { key: "userId", label: "Central auth user id", kind: "text", required: true, full: true },
      { key: "employeeNo", label: "Employee no.", kind: "text", required: true },
      { key: "displayName", label: "Name", kind: "text", required: true },
      { key: "title", label: "Title", kind: "text" },
      { key: "homeStoreId", label: "Home store", kind: "select", lookup: "stores" },
      { key: "phone", label: "Phone", kind: "text" },
    ],
  },
  tenders: {
    title: "Payment methods",
    subtitle: "The ways a customer can pay at the till.",
    noun: "payment method",
    route: "payments/tender-types",
    status: { key: "isActive", options: ACTIVE_OPTS },
    toggle: "active",
    defaults: () => ({ isActive: true, glKey: "Tender.Clearing" }),
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "kind", label: "Type" },
      { key: "glKey", label: "Ledger key", kind: "mono" },
      activeCol,
    ],
    fields: [
      { key: "code", label: "Code", kind: "text", required: true },
      { key: "name", label: "Name", kind: "text", required: true },
      { key: "kind", label: "Type", kind: "select", options: st("Cash", "Card", "Wallet", "GiftCard", "LoyaltyPoints", "Bnpl", "Cheque", "Voucher", "StoreCredit", "BankTransfer", "MobileMoney"), required: true },
      { key: "glKey", label: "Ledger key", kind: "text", required: true },
    ],
  },
  jurisdictions: {
    title: "Tax jurisdictions",
    subtitle: "Where a tax applies, for example a country or state. Tax rates are set per jurisdiction.",
    noun: "jurisdiction",
    route: "tax/jurisdictions",
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true },
      { key: "code", label: "Code", kind: "text" },
    ],
  },
  taxCategories: {
    title: "Tax categories",
    subtitle: "Groups of products taxed the same way, for example standard or zero-rated.",
    noun: "tax category",
    route: "tax/categories",
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true },
      { key: "code", label: "Code", kind: "text" },
    ],
  },
  taxrates: {
    title: "Tax rates",
    subtitle: "The rate charged for each jurisdiction and product tax category.",
    noun: "tax rate",
    route: "tax/rates",
    lookups: ["jurisdictions", "taxCategories"],
    defaults: () => ({ glKey: "Tax.Payable" }),
    cols: [
      { key: "name", label: "Name" },
      { key: "jurisdiction", label: "Jurisdiction", kind: "lookup", lookup: "jurisdictions", get: (r) => r.jurisdictionId },
      { key: "category", label: "Tax category", kind: "lookup", lookup: "taxCategories", get: (r) => r.taxCategoryId },
      { key: "rate", label: "Rate", kind: "pct", right: true, get: (r) => Math.round(num(r.rate) * 10000) / 100 },
      { key: "validFromUtc", label: "From", kind: "date" },
      { key: "validToUtc", label: "To", kind: "date" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "rate", label: "Rate (%)", kind: "number", required: true, pct: true },
      { key: "jurisdictionId", label: "Jurisdiction", kind: "select", lookup: "jurisdictions", required: true },
      { key: "taxCategoryId", label: "Tax category", kind: "select", lookup: "taxCategories", required: true },
      { key: "validFromUtc", label: "Starts", kind: "datetime", required: true },
      { key: "validToUtc", label: "Ends", kind: "datetime" },
    ],
    validate: (v) => (num(v.rate) < 0 || num(v.rate) > 1 ? "Rate must be between 0 and 100." : null),
  },
  fx: {
    title: "Exchange rate overrides",
    subtitle: "Your own rate wins over the market rate while it is valid.",
    noun: "rate override",
    route: "admin/fx-overrides",
    cols: [
      { key: "fromCurrency", label: "From" },
      { key: "toCurrency", label: "To" },
      { key: "rate", label: "Rate", kind: "num", right: true },
      { key: "validFromUtc", label: "Valid from", kind: "date" },
      { key: "validToUtc", label: "Valid to", kind: "date" },
      { key: "reason", label: "Reason" },
    ],
    fields: [
      { key: "fromCurrency", label: "From currency", kind: "text", required: true },
      { key: "toCurrency", label: "To currency", kind: "text", required: true },
      { key: "rate", label: "Rate", kind: "number", required: true },
      { key: "validFromUtc", label: "Valid from", kind: "datetime", required: true },
      { key: "validToUtc", label: "Valid to", kind: "datetime" },
      { key: "reason", label: "Reason", kind: "text", full: true },
    ],
    validate: (v) => (num(v.rate) > 0 ? null : "Rate must be greater than zero."),
  },
  categories: {
    title: "Categories",
    subtitle: "The groups shown as tabs on the point of sale.",
    noun: "category",
    route: "catalog/categories",
    toggle: "active",
    defaults: () => ({ approvalStatus: "Approved", isActive: true }),
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "categoryRole", label: "Role" },
      activeCol,
    ],
    fields: [
      { key: "code", label: "Code", kind: "text", required: true },
      { key: "name", label: "Name", kind: "text", required: true },
    ],
  },
  suppliers: {
    title: "Suppliers",
    subtitle: "Who you buy stock from.",
    noun: "supplier",
    route: "procurement/suppliers",
    status: { key: "isActive", options: ACTIVE_OPTS },
    toggle: "active",
    defaults: (cur) => ({ currencyCode: cur, countryCode: "NG", isActive: true }),
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "paymentTerms", label: "Payment terms" },
      activeCol,
    ],
    fields: [
      { key: "code", label: "Code", kind: "text", required: true },
      { key: "name", label: "Name", kind: "text", required: true },
      { key: "phone", label: "Phone", kind: "text" },
      { key: "email", label: "Email", kind: "text" },
      { key: "paymentTerms", label: "Payment terms", kind: "select", options: st("COD", "NET7", "NET15", "NET30"), required: true },
    ],
  },
  glmappings: {
    title: "Ledger accounts",
    subtitle: "Which accounting account each retail posting key uses.",
    noun: "mapping",
    route: "integration/gl-mappings",
    cols: [
      { key: "key", label: "Posting key", kind: "mono" },
      { key: "accountCode", label: "Account code", kind: "mono" },
      { key: "description", label: "Description" },
    ],
    fields: [
      { key: "key", label: "Posting key", kind: "text", required: true },
      { key: "accountCode", label: "Account code", kind: "text", required: true },
      { key: "description", label: "Description", kind: "text", full: true },
    ],
  },
  translations: {
    title: "Translations",
    subtitle: "Override any message or label for a language.",
    noun: "translation",
    route: "admin/translations",
    cols: [
      { key: "culture", label: "Language", kind: "mono" },
      { key: "key", label: "Key", kind: "mono" },
      { key: "value", label: "Text" },
    ],
    fields: [
      { key: "culture", label: "Language", kind: "text", required: true, hint: "For example en or fr" },
      { key: "key", label: "Key", kind: "text", required: true },
      { key: "value", label: "Text", kind: "text", required: true, full: true },
    ],
  },
};

/** Sidebar structure for Settup.tsx. */
export const RETAIL_SETUP_GROUPS: { title: string; items: { key: string; title: string }[] }[] = [
  { title: "Stores & devices", items: [{ key: "stores", title: "Stores" }, { key: "registers", title: "Registers" }, { key: "associates", title: "Staff" }] },
  {
    title: "Sales & tax",
    items: [
      { key: "tenders", title: "Payment methods" },
      { key: "jurisdictions", title: "Tax jurisdictions" },
      { key: "taxCategories", title: "Tax categories" },
      { key: "taxrates", title: "Tax rates" },
      { key: "fx", title: "Exchange rate overrides" },
    ],
  },
  { title: "Catalog & supply", items: [{ key: "categories", title: "Categories" }, { key: "suppliers", title: "Suppliers" }] },
  { title: "Retail system", items: [{ key: "glmappings", title: "Ledger accounts" }, { key: "translations", title: "Translations" }] },
];

/* ---------- helpers ---------- */

const PAGE_SIZE = 25;
const pad2 = (n: number) => String(n).padStart(2, "0");
const toLocalInput = (iso: any): string => {
  if (!iso) return "";
  const d = new Date(String(iso));
  if (isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const initial = (f: Fld, rec: Row | null, defaults: Record<string, any>): string => {
  const raw = rec ? rec[f.key] : defaults[f.key];
  if (raw === undefined || raw === null) return "";
  if (f.kind === "datetime") return toLocalInput(raw);
  if (f.pct) return String(Math.round(num(raw) * 10000) / 100);
  return String(raw);
};
const TONE: Record<string, string> = { Active: "green", Open: "green", Inactive: "grey", Offline: "amber", Maintenance: "amber", Retired: "grey" };
const Pill: React.FC<{ text: string }> = ({ text }) => <span className={`${styles.accPill} ${styles["accTone_" + (TONE[text] || "grey")]}`}>{spaced(text)}</span>;

/* ---------- record dialog ---------- */

const RecordDialog: React.FC<{
  title: string;
  submit: string;
  fields: Fld[];
  rec: Row | null;
  defaults: Record<string, any>;
  lk: Partial<Record<Lk, Map<string, string>>>;
  onClose: () => void;
  onSave: (values: Record<string, any>) => Promise<string | null>;
}> = ({ title, submit, fields, rec, defaults, lk, onClose, onSave }) => {
  const [v, setV] = useState<Record<string, string>>(() => Object.fromEntries(fields.map((f) => [f.key, initial(f, rec, defaults)])));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const go = async () => {
    if (busy) return;
    const out: Record<string, any> = {};
    for (const f of fields) {
      const raw = (v[f.key] ?? "").trim();
      if (f.required && !raw) return setErr(`${f.label} is required.`);
      if (f.kind === "number") {
        if (raw !== "" && !Number.isFinite(Number(raw))) return setErr(`${f.label} must be a number.`);
        out[f.key] = raw === "" ? null : f.pct ? Number(raw) / 100 : Number(raw);
      } else if (f.kind === "datetime") {
        out[f.key] = raw === "" ? null : new Date(raw).toISOString();
      } else {
        out[f.key] = raw === "" ? null : raw;
      }
    }
    setBusy(true);
    setErr(null);
    const bad = await onSave(out);
    setBusy(false);
    if (bad) setErr(bad);
  };

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface className={styles.accDialog}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            {title}
          </DialogTitle>
          <DialogContent>
            <div className={styles.accForm}>
              {fields.map((f) => {
                const opts: Opt[] = f.lookup ? Array.from((lk[f.lookup] ?? new Map<string, string>()).entries()).map(([k, l]) => ({ v: k, l })) : f.options ?? [];
                return (
                  <div key={f.key} className={f.full ? styles.accFormFull : undefined}>
                    <Field label={f.label} required={!!f.required} hint={f.hint}>
                      {f.kind === "select" ? (
                        <Select value={v[f.key] ?? ""} onChange={(_, d) => setV({ ...v, [f.key]: d.value })}>
                          <option value="">{f.required ? "Select…" : "None"}</option>
                          {opts.map((o) => (
                            <option key={o.v} value={o.v}>
                              {o.l}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <Input
                          type={f.kind === "number" ? "number" : f.kind === "datetime" ? "datetime-local" : "text"}
                          value={v[f.key] ?? ""}
                          onChange={(_, d) => setV({ ...v, [f.key]: d.value })}
                        />
                      )}
                    </Field>
                  </div>
                );
              })}
            </div>
            {err && (
              <div className={styles.accDlgErr} role="alert">
                {err}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <DialogTrigger action="close">
              <Button>Cancel</Button>
            </DialogTrigger>
            <Button appearance="primary" disabled={busy} onClick={go}>
              {busy ? "Working…" : submit}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

/* ---------- the page ---------- */

interface SettupRetailProps {
  /** a key from RETAIL_SETUP_GROUPS, for example "registers" */
  entityKey: string;
}

const SettupRetail: React.FC<SettupRetailProps> = ({ entityKey }) => {
  const ent = ENTITIES[entityKey];
  const toasterId = useId("retail-setup-toaster");
  const { dispatchToast } = useToastController(toasterId);
  const notify = useCallback(
    (message: string, intent: Intent = "success") =>
      dispatchToast(
        <Toast>
          <ToastTitle>{message}</ToastTitle>
        </Toast>,
        { intent, position: "top-end", timeout: 4000 }
      ),
    [dispatchToast]
  );

  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [statusF, setStatusF] = useState("");
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
  const [lk, setLk] = useState<Partial<Record<Lk, Map<string, string>>>>({});
  const [currency, setCurrency] = useState("NGN");
  const [form, setForm] = useState<{ rec: Row | null } | null>(null);
  const [del, setDel] = useState<Row | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const store = useMemo(() => (ent ? crud<Row>(ent.route) : null), [ent]);

  // new screen: start from a clean list
  useEffect(() => {
    setQ("");
    setDq("");
    setStatusF("");
    setSort(null);
    setPage(1);
    setForm(null);
    setDel(null);
    setRows([]);
  }, [entityKey]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDq(q.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(t);
  }, [q]);

  // the tenant's base currency fills in new stores, staff and suppliers
  useEffect(() => {
    let live = true;
    crud<Row>("admin/tenant-settings")
      .list({ pageSize: 1 })
      .then((p) => {
        const c = p.items[0]?.baseCurrency as string | undefined;
        if (live && c) setCurrency(c);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  // names behind the store / jurisdiction / tax category pickers
  const lookupKey = (ent?.lookups ?? []).join(",");
  const loadLookups = useCallback(async () => {
    if (!ent?.lookups?.length) return;
    const out: Partial<Record<Lk, Map<string, string>>> = {};
    await Promise.all(
      ent.lookups.map(async (key) => {
        try {
          const def = LOOKUPS[key];
          const p = await crud<Row>(def.route).list({ pageSize: 200 });
          out[key] = new Map(p.items.map((r) => [r.id, def.label(r)] as [string, string]));
        } catch {
          out[key] = new Map();
        }
      })
    );
    setLk(out);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupKey]);
  useEffect(() => {
    setLk({});
    loadLookups();
  }, [loadLookups]);

  const reload = useCallback(async () => {
    if (!ent || !store) return;
    setLoading(true);
    setLoadErr(null);
    try {
      const query: Record<string, any> = { page, pageSize: PAGE_SIZE };
      if (dq) query.search = dq;
      if (sort) query.sort = sort.dir === -1 ? `-${sort.key}` : sort.key;
      if (ent.status && statusF) query[ent.status.key] = statusF;
      const data = await store.list(query);
      setRows(data.items);
      setTotal(data.totalCount ?? data.items.length);
      setPages(Math.max(1, data.totalPages ?? 1));
    } catch (e) {
      setLoadErr(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [ent, store, page, dq, sort, statusF]);

  useEffect(() => {
    reload();
  }, [reload]);

  const defaults = useMemo(() => (ent?.defaults ? ent.defaults(currency) : {}), [ent, currency]);

  const submit = async (values: Record<string, any>): Promise<string | null> => {
    if (!ent || !store) return null;
    const bad = ent.validate ? ent.validate(values) : null;
    if (bad) return bad;
    try {
      if (form?.rec) {
        await store.update(form.rec.id, { ...values, rowVersion: form.rec.rowVersion });
        notify(`${cap(ent.noun)} saved`);
      } else {
        await store.create({ ...defaults, ...values });
        notify(`${cap(ent.noun)} created`);
      }
      setForm(null);
      await reload();
      return null;
    } catch (e) {
      return errorMessage(e);
    }
  };

  const flip = async (r: Row) => {
    if (!ent || !store || busyId) return;
    setBusyId(r.id);
    try {
      let patch: Record<string, any>;
      let msg: string;
      const name = String(r.name ?? r.displayName ?? r.code ?? "Item");
      if (ent.toggle === "register") {
        const on = r.status === "Active";
        patch = { status: on ? "Retired" : "Active" };
        msg = `${name} ${on ? "retired" : "activated"}`;
      } else {
        const on = r.isActive !== false;
        patch = { isActive: !on };
        msg = `${name} ${on ? "deactivated" : "activated"}`;
      }
      await store.update(r.id, { ...patch, rowVersion: r.rowVersion });
      notify(msg);
      await reload();
    } catch (e) {
      notify(errorMessage(e), "error");
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!del || !store || !ent) return;
    setBusyId(del.id);
    try {
      await store.remove(del.id);
      notify(`${cap(ent.noun)} deleted`);
      setDel(null);
      await reload();
    } catch (e) {
      notify(errorMessage(e), "error");
      setDel(null);
    } finally {
      setBusyId(null);
    }
  };

  if (!ent) return <div className={styles.accWrap}>This setup screen isn't available.</div>;

  const cell = (c: Col, r: Row): React.ReactNode => {
    const v = c.get ? c.get(r) : r[c.key];
    if (v === null || v === undefined || v === "") return "";
    if (c.kind === "status") return <Pill text={String(v)} />;
    if (c.kind === "lookup") return lk[c.lookup as Lk]?.get(String(v)) ?? "";
    if (c.kind === "pct") return `${num(v)}%`;
    if (c.kind === "num") return num(v).toLocaleString("en-US", { maximumFractionDigits: 6 });
    if (c.kind === "date") return String(v).slice(0, 10);
    return String(v);
  };

  const hasActions = true;
  const firstRun = loading && rows.length === 0;

  return (
    <div className={styles.accWrap}>
      <Toaster toasterId={toasterId} />

      <header className={styles.accHead}>
        <div>
          <h2 className={styles.accTitle}>{ent.title}</h2>
          <p className={styles.accSub}>{ent.subtitle}</p>
        </div>
        <Button appearance="primary" icon={<Add20Regular />} onClick={() => setForm({ rec: null })} disabled={firstRun}>
          New {ent.noun}
        </Button>
      </header>

      {loadErr && (
        <div className={styles.accErr} role="alert">
          {loadErr}
          <Button size="small" appearance="subtle" onClick={reload}>
            Try again
          </Button>
        </div>
      )}

      <div className={styles.accTiles}>
        <div className={styles.accTile}>
          <span className={styles.accTileLabel}>{ent.title}</span>
          <span className={styles.accTileValue}>{firstRun ? "…" : total.toLocaleString("en-US")}</span>
          <span className={styles.accTileNote}>{dq || statusF ? "Matching your filters" : "In total"}</span>
        </div>
      </div>

      <section className={styles.accCard}>
        <div className={styles.accToolbar}>
          <div className={styles.accSearch}>
            <Search20Regular />
            <input className={styles.accSearchInput} placeholder={`Search ${ent.title.toLowerCase()}`} value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search" />
          </div>
          {ent.status && (
            <select
              className={styles.accFilter}
              value={statusF}
              onChange={(e) => {
                setStatusF(e.target.value);
                setPage(1);
              }}
              aria-label="Filter by status"
            >
              <option value="">All statuses</option>
              {ent.status.options.map((o) => (
                <option key={o.v} value={o.v}>
                  {o.l}
                </option>
              ))}
            </select>
          )}
          <span className={styles.accSpacer} />
          <span className={styles.accCount}>
            Page {page} of {pages}
          </span>
          <Button appearance="subtle" icon={<ChevronLeft20Regular />} disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)} aria-label="Previous page" />
          <Button appearance="subtle" icon={<ChevronRight20Regular />} disabled={page >= pages || loading} onClick={() => setPage((p) => p + 1)} aria-label="Next page" />
          <Button appearance="subtle" icon={<ArrowSync20Regular />} onClick={reload} disabled={loading} aria-label="Refresh" title="Refresh" />
        </div>

        <div className={styles.accTableWrap}>
          <table className={styles.accTable}>
            <thead>
              <tr>
                {ent.cols.map((c) => (
                  <th
                    key={c.key}
                    className={c.right ? styles.accRight : undefined}
                    onClick={() => {
                      setSort((p) => (p && p.key === c.key ? (p.dir === 1 ? { key: c.key, dir: -1 } : null) : { key: c.key, dir: 1 }));
                      setPage(1);
                    }}
                    aria-sort={sort?.key === c.key ? (sort.dir === 1 ? "ascending" : "descending") : "none"}
                  >
                    {c.label}
                    <span className={styles.accSortMark}>{sort?.key === c.key ? (sort.dir === 1 ? " ▲" : " ▼") : ""}</span>
                  </th>
                ))}
                {hasActions && <th className={styles.accRight} aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {firstRun &&
                [0, 1, 2, 3, 4].map((i) => (
                  <tr key={i}>
                    <td colSpan={ent.cols.length + 1}>
                      <span className={styles.accSk} style={{ width: `${60 + ((i * 11) % 30)}%`, height: 12 }} />
                    </td>
                  </tr>
                ))}
              {rows.map((r) => (
                <tr key={r.id} className={r.isActive === false || r.status === "Retired" ? styles.accRowOff : undefined}>
                  {ent.cols.map((c) => (
                    <td key={c.key} className={`${c.right ? styles.accRight : ""} ${c.kind === "mono" ? styles.accMono : ""}`}>
                      {cell(c, r)}
                    </td>
                  ))}
                  <td className={styles.accRight}>
                    <span className={styles.accActions}>
                      <button className={styles.accIconBtn} title="Edit" aria-label="Edit" onClick={() => setForm({ rec: r })}>
                        <Edit20Regular />
                      </button>
                      {ent.toggle && (
                        <button
                          className={styles.accIconBtn}
                          disabled={busyId === r.id}
                          title={ent.toggle === "register" ? (r.status === "Active" ? "Retire" : "Activate") : r.isActive === false ? "Activate" : "Deactivate"}
                          aria-label="Switch on or off"
                          onClick={() => flip(r)}
                        >
                          {(ent.toggle === "register" ? r.status !== "Active" : r.isActive === false) ? <Checkmark20Regular /> : <Dismiss20Regular />}
                        </button>
                      )}
                      <button className={`${styles.accIconBtn} ${styles.accIconDanger}`} title="Delete" aria-label="Delete" onClick={() => setDel(r)}>
                        <Delete20Regular />
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && rows.length === 0 && !loadErr && (
            <div className={styles.accEmpty}>
              {dq || statusF ? "Nothing matches your search." : `No ${ent.title.toLowerCase()} yet.`}
              {!dq && !statusF && (
                <Button appearance="primary" size="small" icon={<Add20Regular />} onClick={() => setForm({ rec: null })}>
                  New {ent.noun}
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {form && (
        <RecordDialog
          key={form.rec?.id ?? "new"}
          title={form.rec ? `Edit ${ent.noun}` : `New ${ent.noun}`}
          submit={form.rec ? "Save changes" : "Create"}
          fields={ent.fields}
          rec={form.rec}
          defaults={defaults}
          lk={lk}
          onClose={() => setForm(null)}
          onSave={submit}
        />
      )}

      {del && (
        <Dialog open onOpenChange={(_, d) => !d.open && setDel(null)}>
          <DialogSurface className={styles.accDialog}>
            <DialogBody>
              <DialogTitle>Delete {ent.noun}?</DialogTitle>
              <DialogContent>
                <b>{String(del.name ?? del.displayName ?? del.code ?? del.key ?? "This record")}</b> will be removed. This can't be undone.
              </DialogContent>
              <DialogActions>
                <DialogTrigger action="close">
                  <Button>Cancel</Button>
                </DialogTrigger>
                <Button appearance="primary" className={styles.accDangerBtn} disabled={busyId === del.id} onClick={confirmDelete}>
                  Delete
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </div>
  );
};

const cap = (s: string) => `${s[0]?.toUpperCase() ?? ""}${s.slice(1)}`;

export default SettupRetail;